import { useState, useEffect, useCallback, useRef, memo } from 'react';
import { Scale, ChevronDown, ChevronUp, Trash2, Camera, X, MapPin, Info, CheckCircle, Image as ImageIcon, Ship, Users, AlertTriangle, Recycle, Sparkles, Loader2 } from 'lucide-react';
import { compressImage } from '../utils/imageCompression';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n';
import TranslatedText from './TranslatedText';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Linee guida smaltimento rifiuti
const WASTE_DISPOSAL_GUIDELINES = [
  {
    emoji: '🟦',
    title: '1. Plastica rigida',
    examples: 'bottiglie, tappi, cassette, contenitori, boe in plastica, frammenti di plastica dura',
    disposal: [
      'Raccolta plastica se pulita o poco sporca',
      'Indifferenziato se molto sporca o mista ad altri materiali'
    ],
    note: 'Oggetti grandi: usa "Segnalazioni"'
  },
  {
    emoji: '🟩',
    title: '2. Reti da pesca e corde',
    examples: 'reti leggere o pesanti, lenze, corde nautiche, filamenti',
    disposal: [
      'Indifferenziato (materiale composito difficile da riciclare)',
      'Ecocentro per reti pesanti o voluminose'
    ],
    note: 'Oggetti grandi: usa "Segnalazioni"'
  },
  {
    emoji: '🟨',
    title: '3. Metalli',
    examples: 'lattine, pezzi di ferro, ganci, moschettoni, parti metalliche',
    disposal: [
      'Raccolta metalli (o multimateriale, secondo il comune)',
      'Ecocentro per pezzi arrugginiti o voluminosi'
    ],
    note: 'Oggetti grandi: usa "Segnalazioni"'
  },
  {
    emoji: '🟫',
    title: '4. Legno lavorato',
    examples: 'cassette, pallet, assi trattate, parti di imbarcazioni',
    disposal: ['Ecocentro'],
    note: 'Oggetti grandi: usa "Segnalazioni"'
  },
  {
    emoji: '🟧',
    title: '5. Materiali misti o non identificabili',
    examples: 'plastica fusa, gomma, spugne sintetiche, scarpe, giocattoli, oggetti composti da più materiali',
    disposal: ['Indifferenziato'],
    note: 'Oggetti grandi: usa "Segnalazioni"'
  },
  {
    emoji: '⚫',
    title: '6. Rifiuti pericolosi',
    examples: 'batterie, bombolette, contenitori di carburante, oli, vernici, residui chimici',
    disposal: ['Ecocentro (mai nei cassonetti)'],
    note: 'Oggetti grandi o potenzialmente pericolosi: usa "Segnalazioni"'
  },
  {
    emoji: '🟤',
    title: '7. Vetro',
    examples: 'bottiglie, frammenti, vasetti',
    disposal: ['Raccolta vetro'],
    note: 'Oggetti grandi: usa "Segnalazioni"'
  },
  {
    emoji: '🟢',
    title: '8. Materiale naturale (da NON rimuovere)',
    examples: 'alghe, posidonia, legni naturali non trattati',
    disposal: ['Non rimuovere: sono fondamentali per l\'ecosistema e la protezione dell\'arenile'],
    note: 'Se spostati accidentalmente, lasciare sul posto o segnalare al comune tramite "Segnalazioni"'
  }
];

const GENERAL_RULES = [
  'Raccogli solo rifiuti artificiali (plastica, metallo, vetro, reti, gomma)',
  'Usa guanti e sacchi resistenti',
  'Se possibile, separa i materiali durante la raccolta',
  'Per oggetti voluminosi, pesanti o potenzialmente pericolosi, utilizza sempre la funzione "Segnalazioni" dell\'app',
  'Il conferimento dei rifiuti raccolti è consentito senza autorizzazioni speciali (Legge SalvaMare)'
];

const MATERIAL_CATEGORIES = [
  { key: 'plastica_monouso', label: 'Plastica Monouso', description: 'Bottiglie, tappi, bicchieri, stoviglie', color: '#3B82F6' },
  { key: 'mozziconi_cotton', label: 'Mozziconi e Cotton Fioc', description: 'Piccoli rifiuti tossici', color: '#F97316' },
  { key: 'attrezzatura_pesca', label: 'Attrezzatura da Pesca', description: 'Reti, lenze, cassette polistirolo', color: '#EF4444' },
  { key: 'polistirolo', label: 'Polistirolo Espanso', description: 'Frammenti e imballaggi', color: '#FBBF24' },
  { key: 'microplastiche', label: 'Microplastiche', description: 'Frammenti sotto 2-3 cm', color: '#8B5CF6' },
  { key: 'vetro_ceramica', label: 'Vetro e Ceramica', description: '', color: '#06B6D4' },
  { key: 'metalli', label: 'Metalli', description: 'Lattine e tappi', color: '#EC4899' },
  { key: 'rifiuti_sanitari', label: 'Rifiuti Sanitari', description: 'Mascherine, blister, presidi medici', color: '#14B8A6' },
  { key: 'ingombranti', label: 'Rifiuti Ingombranti', description: 'Pneumatici, elettrodomestici, fusti', color: '#22C55E' },
  { key: 'altro', label: 'Altro', description: 'Tessuti, gomma, indifferenziato', color: '#78716C' }
];

const DIFFICULTY_OPTIONS = [
  { value: 'facile', label: 'Facile', color: '#10B981' },
  { value: 'medio', label: 'Medio', color: '#F59E0B' },
  { value: 'difficile', label: 'Difficile', color: '#EF4444' }
];

const POLLUTION_OPTIONS = [
  { value: 'poco', label: 'Poco sporco', color: '#10B981' },
  { value: 'medio', label: 'Mediamente sporco', color: '#F59E0B' },
  { value: 'tanto', label: 'Molto sporco', color: '#EF4444' }
];

const initialCategories = MATERIAL_CATEGORIES.reduce((acc, cat) => ({ ...acc, [cat.key]: '' }), {});

const normalizeDecimalInput = (value) => value.replace(',', '.');
const isValidDecimalInput = (value) => value === '' || /^[0-9]*[.,]?[0-9]*$/.test(value);
const parseDecimalValue = (value) => {
  if (!value || value === '') return 0;
  return parseFloat(normalizeDecimalInput(value)) || 0;
};

const DecimalInput = memo(({ id, testId, value, onChange, placeholder, large, borderColor }) => {
  const handleChange = useCallback((e) => {
    if (isValidDecimalInput(e.target.value)) {
      onChange(e.target.value);
    }
  }, [onChange]);

  return (
    <input
      id={id}
      data-testid={testId}
      type="text"
      inputMode="decimal"
      enterKeyHint="done"
      autoComplete="off"
      value={value}
      onChange={handleChange}
      placeholder={placeholder}
      className={`w-full font-bold bg-slate-800/50 border border-slate-700 rounded-lg px-4 py-3 text-white focus:border-cyan-500 focus:outline-none ${large ? 'text-3xl' : 'text-lg'}`}
      style={borderColor ? { borderLeftColor: borderColor, borderLeftWidth: '3px' } : {}}
    />
  );
});
DecimalInput.displayName = 'DecimalInput';

const FlexibleInputForm = memo(({ onSubmit, disabled }) => {
  const { token } = useAuth();
  const { language } = useLanguage();
  const [showLocationDetails, setShowLocationDetails] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const [categories, setCategories] = useState(initialCategories);
  const [totalKg, setTotalKg] = useState(0);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [compressing, setCompressing] = useState(false);
  const [showESGInfo, setShowESGInfo] = useState(false);
  const [showEducationalPopup, setShowEducationalPopup] = useState(true);
  const [showDisposalGuidelines, setShowDisposalGuidelines] = useState(false);
  
  // Stato analisi IA
  const [analyzingPhoto, setAnalyzingPhoto] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [showAiAnalysis, setShowAiAnalysis] = useState(false);
  
  // Nuovi campi dettaglio luogo
  const [beachName, setBeachName] = useState('');
  const [difficulty, setDifficulty] = useState('');
  const [hasSignage, setHasSignage] = useState(null);
  const [pollutionLevel, setPollutionLevel] = useState('');
  const [animalSightings, setAnimalSightings] = useState('');
  
  // Campo ESG Social (ore)
  const [hoursSpent, setHoursSpent] = useState('');
  
  // Fishing for Litter fields
  const [isSeaCollection, setIsSeaCollection] = useState(false);
  const [participantsCount, setParticipantsCount] = useState('1');
  
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  // Calcolo automatico del Totale Raccolta dalla somma delle categorie
  useEffect(() => {
    const categoriesTotal = Object.values(categories).reduce((sum, val) => sum + parseDecimalValue(val), 0);
    setTotalKg(categoriesTotal);
  }, [categories]);

  const handlePhotoSelect = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Seleziona un file immagine');
      return;
    }

    setCompressing(true);
    try {
      const compressed = await compressImage(file, 1024, 0.65);
      setPhoto(compressed.base64);
      setPhotoPreview(compressed.base64);
      toast.success(`Foto compressa: ${compressed.sizeKB}KB`);
    } catch (error) {
      console.error('Compression error:', error);
      toast.error('Errore compressione foto');
    } finally {
      setCompressing(false);
    }
  }, []);

  const removePhoto = useCallback(() => {
    setPhoto(null);
    setPhotoPreview(null);
    setAiAnalysis(null);
    setShowAiAnalysis(false);
    if (cameraInputRef.current) cameraInputRef.current.value = '';
    if (galleryInputRef.current) galleryInputRef.current.value = '';
  }, []);

  // Analisi foto con IA
  const analyzePhotoWithAI = useCallback(async () => {
    if (!photo || !token) return;
    
    setAnalyzingPhoto(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/analyze-waste-image`,
        { image_base64: photo },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // I pesi sono già corretti dal backend (-30%)
      setAiAnalysis(response.data);
      setShowAiAnalysis(true);
      toast.success('Analisi completata!');
    } catch (error) {
      console.error('AI Analysis error:', error);
      toast.error('Errore nell\'analisi. Riprova.');
    } finally {
      setAnalyzingPhoto(false);
    }
  }, [photo, token]);

  // Applica i risultati dell'IA alle categorie
  const applyAiResults = useCallback(() => {
    if (!aiAnalysis?.materials) return;
    
    // Mappa i materiali dell'IA alle categorie del form
    const materialMapping = {
      'Bottiglie PET': 'plastica_monouso',
      'Bottiglie plastica': 'plastica_monouso',
      'Plastica': 'plastica_monouso',
      'Tappi': 'plastica_monouso',
      'Bicchieri': 'plastica_monouso',
      'Sacchetti': 'plastica_monouso',
      'Lattine': 'metalli',
      'Alluminio': 'metalli',
      'Metallo': 'metalli',
      'Lattine alluminio': 'metalli',
      'Vetro': 'vetro_ceramica',
      'Bottiglie vetro': 'vetro_ceramica',
      'Reti': 'attrezzatura_pesca',
      'Reti da pesca': 'attrezzatura_pesca',
      'Lenze': 'attrezzatura_pesca',
      'Cassette polistirolo': 'polistirolo',
      'Polistirolo': 'polistirolo',
      'Mozziconi': 'mozziconi_cotton',
      'Cotton fioc': 'mozziconi_cotton',
      'Microplastiche': 'microplastiche',
      'Mascherine': 'rifiuti_sanitari',
      'Pneumatici': 'ingombranti'
    };
    
    const newCategories = { ...initialCategories };
    
    aiAnalysis.materials.forEach(material => {
      const weightKg = material.estimated_weight_grams / 1000;
      let categoryKey = 'altro';
      
      // Cerca corrispondenza nel mapping
      for (const [keyword, catKey] of Object.entries(materialMapping)) {
        if (material.name.toLowerCase().includes(keyword.toLowerCase())) {
          categoryKey = catKey;
          break;
        }
      }
      
      // Aggiungi al peso esistente
      const currentValue = parseDecimalValue(newCategories[categoryKey]);
      newCategories[categoryKey] = (currentValue + weightKg).toFixed(2).replace('.', ',');
    });
    
    setCategories(newCategories);
    setShowAiAnalysis(false);
    toast.success('Pesi applicati! Verifica e correggi se necessario.');
  }, [aiAnalysis]);

  const handleSubmit = useCallback(() => {
    if (totalKg <= 0) {
      toast.error('Inserisci i Kg raccolti');
      return;
    }
    
    if (!photo) {
      toast.error('La foto della raccolta è obbligatoria per validare il report');
      return;
    }

    const data = {
      categories: MATERIAL_CATEGORIES.reduce((acc, cat) => ({
        ...acc,
        [cat.key]: parseDecimalValue(categories[cat.key])
      }), {}),
      total_kg: totalKg,
      photo: photo,
      // Nuovi campi
      beach_name: beachName || '',
      difficulty: difficulty || '',
      has_signage: hasSignage,
      pollution_level: pollutionLevel || '',
      animal_sightings: animalSightings || '',
      // ESG - Calcolo E basato esclusivamente su Totale Raccolta
      hours_spent: parseDecimalValue(hoursSpent) || 0,
      esg_environmental: parseFloat((totalKg * 0.15).toFixed(2)),
      esg_social: parseDecimalValue(hoursSpent) || 0,
      esg_governance: true,
      // Fishing for Litter fields
      is_sea_collection: isSeaCollection,
      participants_count: parseInt(participantsCount) || 1
    };

    onSubmit(data);
    
    // Reset
    setCategories(initialCategories);
    setShowLocationDetails(false);
    setPhoto(null);
    setPhotoPreview(null);
    setBeachName('');
    setDifficulty('');
    setHasSignage(null);
    setPollutionLevel('');
    setAnimalSightings('');
    setHoursSpent('');
    setIsSeaCollection(false);
    setParticipantsCount('1');
    setShowEducationalPopup(true);
  }, [totalKg, categories, photo, beachName, difficulty, hasSignage, pollutionLevel, animalSightings, hoursSpent, isSeaCollection, participantsCount, onSubmit]);

  const resetForm = useCallback(() => {
    setCategories(initialCategories);
    setPhoto(null);
    setPhotoPreview(null);
    setBeachName('');
    setDifficulty('');
    setHasSignage(null);
    setPollutionLevel('');
    setAnimalSightings('');
    setHoursSpent('');
    setIsSeaCollection(false);
    setParticipantsCount('1');
  }, []);

  // Calcolo ESG Environmental
  const esgEnvironmental = parseFloat((totalKg * 0.15).toFixed(2));

  return (
    <div className="bg-slate-900/60 backdrop-blur-xl border border-cyan-500 rounded-2xl p-6 shadow-[0_0_15px_rgba(6,182,212,0.3)]">
      {/* Pop-up Educativo - Sempre in alto */}
      {showEducationalPopup && (
        <div key={`edu-popup-${language}`} className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center p-4 pt-8 overflow-y-auto" onClick={() => setShowEducationalPopup(false)}>
          <div className="bg-slate-900 border border-amber-500/50 rounded-2xl p-6 max-w-md w-full mt-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-amber-400" />
              </div>
              <h3 className="text-lg font-bold text-amber-400"><TranslatedText text="Importante!" /></h3>
            </div>
            <div className="space-y-3 text-slate-300 text-sm">
              <p>
                <TranslatedText text="Se la raccolta è stata fatta da più persone, solo una persona deve caricare i dati per evitare doppioni nello storico." />
              </p>
              <p>
                <TranslatedText text="Indica il numero di partecipanti nel campo apposito qui sotto per tracciare correttamente l'impatto sociale della raccolta." />
              </p>
            </div>
            
            {/* Pulsante info smaltimento */}
            <button
              type="button"
              onClick={() => setShowDisposalGuidelines(true)}
              className="w-full mt-4 py-3 bg-emerald-600/20 hover:bg-emerald-600/30 border border-emerald-500/50 text-emerald-400 font-semibold rounded-lg flex items-center justify-center gap-2 transition-all"
            >
              <Recycle className="w-5 h-5" />
              <TranslatedText text="Come smaltire i rifiuti" />
              <Info className="w-4 h-4" />
            </button>
            
            <button
              type="button"
              onClick={() => setShowEducationalPopup(false)}
              className="w-full mt-3 py-3 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg"
            >
              <TranslatedText text="Ho capito, procedi" />
            </button>
          </div>
        </div>
      )}

      {/* Pop-up Linee Guida Smaltimento */}
      {showDisposalGuidelines && (
        <div key={`disposal-popup-${language}`} className="fixed inset-0 bg-black/90 z-[60] flex items-start justify-center p-4 overflow-y-auto" onClick={() => setShowDisposalGuidelines(false)}>
          <div className="bg-slate-900 border border-emerald-500/50 rounded-2xl p-6 max-w-lg w-full my-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <Recycle className="w-5 h-5 text-emerald-400" />
                </div>
                <h3 className="text-lg font-bold text-emerald-400"><TranslatedText text="Linee Guida Smaltimento" /></h3>
              </div>
              <button
                type="button"
                onClick={() => setShowDisposalGuidelines(false)}
                className="w-8 h-8 rounded-full bg-slate-800 hover:bg-slate-700 flex items-center justify-center"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
            
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              {WASTE_DISPOSAL_GUIDELINES.map((item, idx) => (
                <div key={idx} className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
                  <h4 className="font-bold text-white mb-2 flex items-center gap-2">
                    <span className="text-xl">{item.emoji}</span>
                    <TranslatedText text={item.title} />
                  </h4>
                  <p className="text-slate-400 text-xs mb-2">
                    <strong><TranslatedText text="Esempi:" /></strong> <TranslatedText text={item.examples} />
                  </p>
                  <div className="text-sm text-slate-300 mb-2">
                    <strong className="text-emerald-400"><TranslatedText text="Dove conferire:" /></strong>
                    <ul className="list-disc list-inside ml-2 mt-1">
                      {item.disposal.map((d, i) => (
                        <li key={i} className="text-slate-400"><TranslatedText text={d} /></li>
                      ))}
                    </ul>
                  </div>
                  <p className="text-xs text-amber-400 italic"><TranslatedText text={item.note} /></p>
                </div>
              ))}
              
              {/* Regole generali */}
              <div className="bg-cyan-500/10 rounded-xl p-4 border border-cyan-500/30">
                <h4 className="font-bold text-cyan-400 mb-3 flex items-center gap-2">
                  📌 <TranslatedText text="Regole Generali" />
                </h4>
                <ul className="space-y-2">
                  {GENERAL_RULES.map((rule, idx) => (
                    <li key={idx} className="text-slate-300 text-sm flex items-start gap-2">
                      <span className="text-cyan-400 mt-1">•</span>
                      <TranslatedText text={rule} />
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            
            <button
              type="button"
              onClick={() => setShowDisposalGuidelines(false)}
              className="w-full mt-4 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-lg"
            >
              <TranslatedText text="Chiudi" />
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Scale className="w-6 h-6 text-cyan-400" />
          <h3 className="text-sm font-semibold text-cyan-400 uppercase tracking-wider"><TranslatedText text="Nuova Raccolta" /></h3>
        </div>
        {(photo || beachName || Object.values(categories).some(v => v)) && (
          <button type="button" onClick={resetForm} className="text-slate-500 hover:text-red-400">
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Categorie Materiali - Sempre visibile */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-3">
          <label className="text-xs text-slate-400 uppercase tracking-wider">
            <TranslatedText text="Inserisci Kg per categoria" />
          </label>
        </div>
        <p className="text-xs text-slate-500 mb-3"><TranslatedText text="Il Totale Raccolta sarà calcolato automaticamente" /></p>
      </div>

      {/* Foto Raccolta (OBBLIGATORIA) */}
      <div className="mb-4">
        <label className="text-xs text-slate-400 uppercase tracking-wider mb-2 block flex items-center gap-2">
          <Camera className="w-4 h-4 text-cyan-400" />
          <TranslatedText text="Foto Raccolta" /> <span className="text-red-400">*</span>
          <span className="text-[10px] text-slate-500 normal-case">(<TranslatedText text="evidenza digitale obbligatoria" />)</span>
        </label>
        
        {photoPreview ? (
          <div className="relative">
            <img src={photoPreview} alt="Preview" className="w-full h-48 object-cover rounded-lg border-2 border-emerald-500/50" />
            <div className="absolute top-2 left-2 px-2 py-1 bg-emerald-500/90 rounded text-white text-xs font-medium flex items-center gap-1">
              <CheckCircle className="w-3 h-3" />
              <TranslatedText text="Validata" />
            </div>
            <button type="button" onClick={removePhoto} className="absolute top-2 right-2 p-2 bg-red-500/80 rounded-full text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {compressing ? (
              <div className="w-full py-4 border-2 border-dashed border-cyan-500/50 rounded-lg flex flex-col items-center gap-2 bg-cyan-500/5">
                <div className="w-6 h-6 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                <span className="text-sm text-cyan-400"><TranslatedText text="Compressione..." /></span>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => cameraInputRef.current?.click()}
                  className="py-4 border-2 border-dashed border-cyan-500/50 rounded-lg text-slate-400 hover:border-cyan-500 hover:text-cyan-400 transition-all flex flex-col items-center gap-2 bg-cyan-500/5"
                >
                  <Camera className="w-7 h-7" />
                  <span className="text-xs font-medium"><TranslatedText text="Scatta Foto" /></span>
                </button>
                <button
                  type="button"
                  onClick={() => galleryInputRef.current?.click()}
                  className="py-4 border-2 border-dashed border-violet-500/50 rounded-lg text-slate-400 hover:border-violet-500 hover:text-violet-400 transition-all flex flex-col items-center gap-2 bg-violet-500/5"
                >
                  <ImageIcon className="w-7 h-7" />
                  <span className="text-xs font-medium"><TranslatedText text="Galleria" /></span>
                </button>
              </div>
            )}
            <p className="text-xs text-red-400 text-center"><TranslatedText text="Obbligatoria per validare il report" /></p>
          </div>
        )}
        <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handlePhotoSelect} className="hidden" />
        <input ref={galleryInputRef} type="file" accept="image/*" onChange={handlePhotoSelect} className="hidden" />
      </div>

      {/* Pulsante Analisi IA */}
      {photoPreview && (
        <div className="mb-4">
          <button
            type="button"
            onClick={analyzePhotoWithAI}
            disabled={analyzingPhoto}
            className="w-full py-3 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 disabled:opacity-50 text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-all"
          >
            {analyzingPhoto ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <TranslatedText text="Analisi in corso..." />
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                <TranslatedText text="Analizza con IA (stima peso e materiali)" />
              </>
            )}
          </button>
          <p className="text-xs text-slate-500 text-center mt-1">
            <TranslatedText text="L'IA identificherà i materiali e stimerà i pesi" />
          </p>
        </div>
      )}

      {/* Risultati Analisi IA */}
      {showAiAnalysis && aiAnalysis && (
        <div className="mb-4 p-4 bg-gradient-to-br from-violet-500/10 to-purple-500/10 border border-violet-500/30 rounded-xl">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-bold text-violet-400 flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              Analisi IA
            </h4>
            <button
              type="button"
              onClick={() => setShowAiAnalysis(false)}
              className="text-slate-400 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          
          {/* Materiali identificati */}
          {aiAnalysis.materials && aiAnalysis.materials.length > 0 ? (
            <>
              <div className="space-y-2 mb-3">
                {aiAnalysis.materials.map((mat, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm bg-slate-800/50 rounded-lg px-3 py-2">
                    <span className="text-slate-300">
                      {mat.name} {mat.quantity > 1 && `(${mat.quantity})`}
                    </span>
                    <div className="flex items-center gap-3">
                      <span className="text-violet-400 font-medium">
                        ~{mat.estimated_weight_grams >= 1000 
                          ? `${(mat.estimated_weight_grams / 1000).toFixed(2)} kg`
                          : `${mat.estimated_weight_grams} g`
                        }
                      </span>
                      <span className="text-xs text-slate-500 bg-slate-700 px-2 py-0.5 rounded">
                        {mat.disposal_bin}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Totale stimato */}
              <div className="flex items-center justify-between p-3 bg-violet-500/20 rounded-lg mb-3">
                <span className="text-white font-medium">Peso Totale Stimato</span>
                <span className="text-xl font-bold text-violet-400">
                  ~{aiAnalysis.total_estimated_weight_kg} kg
                </span>
              </div>
              
              {/* Confidenza */}
              <div className="flex items-center gap-2 text-xs text-slate-400 mb-3">
                <span>Affidabilità stima:</span>
                <span className={`px-2 py-0.5 rounded ${
                  aiAnalysis.confidence === 'alta' ? 'bg-emerald-500/20 text-emerald-400' :
                  aiAnalysis.confidence === 'media' ? 'bg-amber-500/20 text-amber-400' :
                  'bg-red-500/20 text-red-400'
                }`}>
                  {aiAnalysis.confidence}
                </span>
              </div>
              
              {/* Note */}
              {aiAnalysis.notes && (
                <p className="text-xs text-slate-400 italic mb-3">{aiAnalysis.notes}</p>
              )}
              
              {/* Pulsanti azione */}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={applyAiResults}
                  className="flex-1 py-2 bg-violet-500 hover:bg-violet-600 text-white text-sm font-medium rounded-lg flex items-center justify-center gap-2"
                >
                  <CheckCircle className="w-4 h-4" />
                  Usa questa stima
                </button>
                <button
                  type="button"
                  onClick={() => setShowAiAnalysis(false)}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm rounded-lg"
                >
                  Ignora
                </button>
              </div>
            </>
          ) : (
            <p className="text-slate-400 text-sm">Nessun materiale identificato nella foto.</p>
          )}
        </div>
      )}

      {/* Toggle categorie materiali - Tendina espandibile */}
      <button
        type="button"
        onClick={() => setShowCategories(prev => !prev)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-800/50 rounded-lg border border-cyan-700/50 hover:border-cyan-500/50 mb-4"
      >
        <span className="text-sm text-slate-300 flex items-center gap-2">
          <Scale className="w-4 h-4 text-cyan-400" />
          {showCategories ? 'Nascondi dettaglio materiali' : 'Dettaglio materiali per categoria (opzionale)'}
          {totalKg > 0 && <span className="text-cyan-400 font-bold ml-2">Totale: {totalKg.toFixed(1)} kg</span>}
        </span>
        {showCategories ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
      </button>

      {showCategories && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4 p-4 bg-slate-800/30 rounded-lg border border-slate-700/50">
          {MATERIAL_CATEGORIES.map((cat) => (
            <div key={cat.key}>
              <label className="text-xs text-slate-400 mb-1 block">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: cat.color }} />
                  <span className="font-medium">{cat.label}</span>
                  <span className="text-cyan-400 font-semibold">(kg)</span>
                </span>
                {cat.description && (
                  <span className="text-slate-500 text-[10px] block ml-4">{cat.description}</span>
                )}
              </label>
              <DecimalInput
                id={`kg-input-${cat.key}`}
                testId={`kg-input-${cat.key}`}
                value={categories[cat.key]}
                onChange={(value) => setCategories(prev => ({ ...prev, [cat.key]: value }))}
                placeholder="0,0 kg"
                borderColor={cat.color}
              />
            </div>
          ))}
        </div>
      )}

      {/* Toggle dettagli luogo */}
      <button
        type="button"
        onClick={() => setShowLocationDetails(prev => !prev)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-800/50 rounded-lg border border-emerald-700/50 hover:border-emerald-500/50 mb-4"
      >
        <span className="text-sm text-slate-300 flex items-center gap-2">
          <MapPin className="w-4 h-4 text-emerald-400" />
          {showLocationDetails ? 'Nascondi info luogo' : 'Info luogo e avvistamenti (opzionale)'}
        </span>
        {showLocationDetails ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
      </button>

      {showLocationDetails && (
        <div className="mb-4 p-4 bg-slate-800/30 rounded-lg border border-emerald-700/30 space-y-4">
          {/* Nome spiaggia */}
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-wider mb-1 block">Nome Spiaggia / Luogo</label>
            <input
              type="text"
              value={beachName}
              onChange={(e) => setBeachName(e.target.value)}
              placeholder="Es. Spiaggia delle Dune"
              className="w-full bg-slate-800/50 border border-slate-700 rounded-lg px-4 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none"
            />
          </div>

          {/* Difficoltà */}
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-wider mb-2 block">Difficoltà per raggiungere</label>
            <div className="flex gap-2">
              {DIFFICULTY_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setDifficulty(opt.value)}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                    difficulty === opt.value 
                      ? 'text-white' 
                      : 'bg-slate-800/50 text-slate-400 hover:text-white'
                  }`}
                  style={difficulty === opt.value ? { backgroundColor: opt.color } : {}}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Segnaletica */}
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-wider mb-2 block">Presenza segnaletica?</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setHasSignage(true)}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                  hasSignage === true ? 'bg-emerald-500 text-white' : 'bg-slate-800/50 text-slate-400'
                }`}
              >
                Sì, presente
              </button>
              <button
                type="button"
                onClick={() => setHasSignage(false)}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                  hasSignage === false ? 'bg-red-500 text-white' : 'bg-slate-800/50 text-slate-400'
                }`}
              >
                No, assente
              </button>
            </div>
          </div>

          {/* Livello sporcizia */}
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-wider mb-2 block">Quanto era sporco?</label>
            <div className="flex gap-2">
              {POLLUTION_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setPollutionLevel(opt.value)}
                  className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-all ${
                    pollutionLevel === opt.value 
                      ? 'text-white' 
                      : 'bg-slate-800/50 text-slate-400 hover:text-white'
                  }`}
                  style={pollutionLevel === opt.value ? { backgroundColor: opt.color } : {}}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Avvistamenti */}
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-wider mb-1 block">Avvistamenti animali / uccelli / nidi</label>
            <textarea
              value={animalSightings}
              onChange={(e) => setAnimalSightings(e.target.value)}
              placeholder="Es. Avvistato gabbiano reale, nido di fratino sulla duna..."
              rows={2}
              className="w-full bg-slate-800/50 border border-slate-700 rounded-lg px-4 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none resize-none"
            />
          </div>
        </div>
      )}

      {/* Totale Raccolta - Calcolato automaticamente */}
      <div className="flex items-center justify-between p-4 bg-gradient-to-r from-cyan-500/10 to-emerald-500/10 rounded-lg border border-cyan-500/30 mb-4">
        <div>
          <p className="text-xs text-slate-400 uppercase">Totale Raccolta</p>
          <p className="text-3xl font-bold text-white">{totalKg.toFixed(1).replace('.', ',')} <span className="text-lg text-slate-400">kg</span></p>
          <p className="text-[10px] text-slate-500">Somma automatica delle categorie</p>
        </div>
      </div>

      {/* Raccolta in Mare e Partecipanti */}
      <div className="mb-4 p-4 bg-slate-800/50 rounded-xl border border-orange-500/30">
        <h4 className="text-sm font-bold text-orange-400 uppercase tracking-wider flex items-center gap-2 mb-4">
          <Ship className="w-4 h-4" />
          Dettagli Raccolta
        </h4>
        
        {/* Toggle Raccolta in Mare */}
        <div className="flex items-center justify-between mb-4 p-3 bg-slate-900/50 rounded-lg">
          <div className="flex items-center gap-2">
            <Ship className="w-5 h-5 text-orange-400" />
            <span className="text-white text-sm">Raccolta in mare (Fishing for Litter)</span>
          </div>
          <button
            type="button"
            onClick={() => setIsSeaCollection(!isSeaCollection)}
            className={`relative w-12 h-6 rounded-full transition-all ${isSeaCollection ? 'bg-orange-500' : 'bg-slate-700'}`}
          >
            <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${isSeaCollection ? 'left-7' : 'left-1'}`} />
          </button>
        </div>
        
        {/* Numero Partecipanti */}
        <div className="p-3 bg-slate-900/50 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-5 h-5 text-emerald-400" />
            <span className="text-white text-sm">Numero partecipanti alla raccolta</span>
          </div>
          <input
            type="number"
            min="1"
            max="999"
            value={participantsCount}
            onChange={(e) => setParticipantsCount(e.target.value)}
            className="w-full bg-slate-800/50 border border-emerald-500/30 rounded px-3 py-2 text-white text-lg font-bold focus:border-emerald-500 focus:outline-none"
          />
          <p className="text-[10px] text-slate-500 mt-1">Quante persone hanno partecipato a questa raccolta?</p>
        </div>
      </div>

      {/* MODULO ESG */}
      <div className="mb-4 p-4 bg-slate-800/50 rounded-xl border border-violet-500/30">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-bold text-violet-400 uppercase tracking-wider flex items-center gap-2">
            Calcolo ESG
          </h4>
          <button
            type="button"
            onClick={() => setShowESGInfo(true)}
            className="w-6 h-6 rounded-full bg-violet-500/20 flex items-center justify-center hover:bg-violet-500/40 transition-all"
          >
            <Info className="w-3 h-3 text-violet-400" />
          </button>
        </div>
        
        <div className="space-y-3">
          {/* E - Environmental - Formula: Totale Raccolta × 0.15 */}
          <div className="bg-gradient-to-r from-emerald-500/10 to-transparent rounded-lg p-3 border-l-3 border-emerald-500">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="bg-emerald-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">E</span>
                <span className="text-emerald-400 text-xs font-medium">Environmental</span>
              </div>
              <span className="text-emerald-400 font-bold">{esgEnvironmental.toFixed(2)} kg CO₂eq</span>
            </div>
            <p className="text-slate-500 text-[10px] mt-1 ml-6">= Totale Raccolta × 0.15</p>
          </div>
          
          {/* S - Social */}
          <div className="bg-gradient-to-r from-blue-500/10 to-transparent rounded-lg p-3 border-l-3 border-blue-500">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="bg-blue-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">S</span>
                <span className="text-blue-400 text-xs font-medium">Social</span>
              </div>
            </div>
            <input
              type="text"
              inputMode="decimal"
              value={hoursSpent}
              onChange={(e) => {
                if (isValidDecimalInput(e.target.value)) {
                  setHoursSpent(e.target.value);
                }
              }}
              placeholder="0"
              className="w-full bg-slate-900/50 border border-blue-500/30 rounded px-3 py-2 text-white text-sm focus:border-blue-500 focus:outline-none"
            />
            <p className="text-slate-500 text-[10px] mt-1">Ore dedicate alla raccolta</p>
          </div>
          
          {/* G - Governance */}
          <div className="bg-gradient-to-r from-amber-500/10 to-transparent rounded-lg p-3 border-l-3 border-amber-500">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">G</span>
                <span className="text-amber-400 text-xs font-medium">Governance</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span className="text-emerald-400 text-xs font-bold">100% Tracciabilità</span>
              </div>
            </div>
            <p className="text-slate-500 text-[10px] mt-1 ml-6">GPS + Foto = Dato verificato</p>
          </div>
        </div>
      </div>

      {/* ESG Info Modal */}
      {showESGInfo && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowESGInfo(false)}>
          <div className="bg-slate-900 border border-violet-500/50 rounded-2xl p-6 max-w-md w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-violet-400">Cosa sono i parametri ESG</h3>
              <button type="button" onClick={() => setShowESGInfo(false)} className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center">
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
            <div className="space-y-4 text-sm text-slate-300">
              <p>I criteri ESG (Environmental, Social, Governance) sono gli standard globali utilizzati per valutare l'impatto di un'attività sulla sostenibilità.</p>
              <div>
                <h4 className="text-emerald-400 font-bold">Environmental (Ambiente)</h4>
                <p className="text-slate-400">Indica il beneficio diretto per il pianeta. In questo report, è calcolato in base alla plastica rimossa che, se degradata, rilascerebbe gas serra. Formula: Kg × 0.15 = CO₂eq evitata.</p>
              </div>
              <div>
                <h4 className="text-blue-400 font-bold">Social (Sociale)</h4>
                <p className="text-slate-400">Misura l'impegno civico e il valore del tempo dedicato dai volontari alla tutela del bene comune.</p>
              </div>
              <div>
                <h4 className="text-amber-400 font-bold">Governance (Trasparenza)</h4>
                <p className="text-slate-400">Garantisce che il dato sia reale, verificato tramite posizione GPS e documentazione fotografica incontrovertibile.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Submit */}
      <button
        type="button"
        data-testid="submit-collection-btn"
        onClick={handleSubmit}
        disabled={totalKg <= 0 || disabled || compressing || !photo}
        className="w-full py-3 bg-cyan-500 hover:bg-cyan-600 text-white font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {!photo ? <TranslatedText text="Aggiungi Foto per Salvare" /> : <TranslatedText text="Salva Raccolta con Validazione" />}
      </button>
    </div>
  );
});

FlexibleInputForm.displayName = 'FlexibleInputForm';
export { FlexibleInputForm, MATERIAL_CATEGORIES };
export default FlexibleInputForm;
